// Billing: the company's prepaid credit (Brúnó, 2026-10-08). A brand buys
// credit in dollars through Stripe, each paid month of its plan adds the same
// amount (2026-10-10), and its campaigns spend it: each video at the price the
// campaign shows, a view bonus when a video reaches it. Payments, their
// invoices and every change to the balance are listed here; the card and
// cancelling are in Stripe's billing portal.
//
// Stripe's redirect back proves nothing: the balance moves when Stripe's
// signed webhook arrives, so a return from Checkout reloads until it does.

import {useCallback, useEffect, useState} from 'react';
import type {FormEvent} from 'react';
import {Link, Navigate, useNavigate, useSearchParams} from 'react-router-dom';
import type {BrandBilling, BrandCreditEntry, BrandPayment} from '../../shared/brand-api';
import {Icon} from '../creator/components/Icon';
import {describe, fetchBilling, openBillingPortal, startTopUp} from './api';
import {InfoTip} from './info-tip';
import {PlanLine} from './plan-credit';
import {usePageTitle} from './page-title';
import {useBrand} from './store';
import './billing.css';

function usd(cents: number, whole = false): string {
  return new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD', minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2}).format(cents / 100);
}

function day(value: string | null): string {
  return value ? new Date(value).toLocaleDateString(undefined, {day: 'numeric', month: 'short', year: 'numeric'}) : '';
}

const ENTRY_LABEL: Record<BrandCreditEntry['kind'], string> = {
  top_up: 'Credit purchase', spend: 'Video', bonus: 'View bonus', refund: 'Refund',
  dispute: 'Payment disputed', dispute_won: 'Dispute closed in your favour', adjustment: 'Adjustment by Earn',
};

/** Free credit from the team is an adjustment whose note starts so (data/credit-gifts.ts). */
const GIFT_NOTE = 'Free credit: ';

/** A purchase and a paid month of the plan both add credit; the entry's note says which. */
function entryLabel(entry: BrandCreditEntry): string {
  if (entry.kind === 'adjustment' && entry.note.startsWith(GIFT_NOTE)) return 'Free credit from Earn';
  return entry.kind === 'top_up' && entry.note ? entry.note : ENTRY_LABEL[entry.kind];
}

const PAYMENT_LABEL: Record<BrandPayment['status'], string> = {
  open: 'Not finished', processing: 'Bank payment on its way', paid: 'Paid', failed: 'Payment failed', expired: 'Not finished',
};

/** How long a return from Checkout keeps checking for Stripe's confirmation. */
const CONFIRM_TRIES = 10;

export function BillingPage() {
  const {data, toast, reload} = useBrand();
  usePageTitle('Billing');
  const [billing, setBilling] = useState<BrandBilling | null>(null);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState<'' | 'pay' | 'portal'>('');
  const [params, setParams] = useSearchParams();
  const returning = params.has('checkout');
  // Sent here by a campaign that needs more credit: that amount, in whole dollars, to start with,
  // and the campaign to go back to once the payment lands (credit-dialog.tsx).
  const need = Number(params.get('need') || 0);
  const backParam = params.get('back') || '';
  const back = /^\/campaigns\/[0-9a-f-]{36}\?tab=(applicants|invite)$/.test(backParam) ? backParam : '';
  const navigate = useNavigate();
  useEffect(() => {
    if (need > 0 && billing && !amount) setAmount(String(Math.max(billing.top_up.min_cents, Math.ceil(need / 100) * 100) / 100));
  }, [need, billing, amount]);

  const load = useCallback(async () => {
    try {
      const next = (await fetchBilling()).billing;
      setBilling(next);
      return next;
    } catch (caught) {
      toast(describe(caught), true);
      return null;
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  // Back from Checkout: the payment shows once Stripe confirms it, usually within seconds, and
  // sometimes before the page loads. The purchase is found by its Checkout, so either way counts.
  useEffect(() => {
    if (!returning || !billing) return;
    const checkout = params.get('checkout') || '';
    const state = (current: BrandBilling) => current.payments.find((payment) => payment.checkout_id === checkout)?.status;
    let tries = 0;
    let done = false;
    const settle = async (current: BrandBilling | null) => {
      const status = current ? state(current) : undefined;
      if (status !== 'paid' && status !== 'processing' && tries < CONFIRM_TRIES) return false;
      done = true;
      if (status === 'paid') {
        await reload(true);
        if (back) { toast('Payment received. You can bring the creator in now.'); navigate(back, {replace: true}); return true; }
        toast('Payment received. Your credit is ready.');
      }
      // A bank payment takes days; until then, or when it is slow, the way back to the campaign stays.
      setParams(back ? {back} : {}, {replace: true});
      return true;
    };
    void settle(billing);
    const timer = window.setInterval(async () => {
      if (done) { window.clearInterval(timer); return; }
      tries += 1;
      if (await settle(await load())) window.clearInterval(timer);
    }, 2000);
    return () => window.clearInterval(timer);
    // Started once per return; `billing` only gates the first run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returning, Boolean(billing)]);

  if (!data) return null;
  if (data.agreement.status !== 'signed') return <Navigate to={data.profile.complete ? '/agreement' : '/company'} replace />;
  if (!billing) return <div className="fullscreen-center"><div className="loader" /></div>;

  const cents = Math.round(Number(amount || 0) * 100);
  async function pay(event: FormEvent) {
    event.preventDefault();
    setBusy('pay');
    try {
      const {url} = await startTopUp(cents, back);
      window.location.assign(url);
    } catch (caught) {
      toast(describe(caught), true);
      setBusy('');
    }
  }
  async function portal() {
    setBusy('portal');
    try { window.location.assign((await openBillingPortal()).url); }
    catch (caught) { toast(describe(caught), true); setBusy(''); }
  }

  const pending = billing.payments.filter((item) => item.status === 'processing');
  const {min_cents: min, max_cents: max, card_max_cents: cardMax} = billing.top_up;
  // Brúnó, 2026-10-15: the plan, the credit and buying more are one block that fits the first
  // screen; what the numbers and the limits mean sits behind info icons.
  // "Add credit" elsewhere in the portal links here with ?add, and the amount is ready to type.
  return <div className="page billing-page">{back ? <Link className="back-link" to={back}><Icon name="arrowLeft" size={15} />Back to the campaign</Link> : null}<header className="page-head billing-head"><h1>Credit and invoices<InfoTip wide text="Every paid month of your plan comes back as credit, and you can add more any time. Each creator you bring in sets aside the price of their videos; you pay only for the work you approve, and the rest comes back when the campaign ends. A view bonus comes off your free credit when a video reaches it." /></h1>{billing.payments.length || billing.subscription ? <button className="btn btn-light btn-sm" type="button" disabled={busy === 'portal'} onClick={() => void portal()}><Icon name="receipt" size={15} />{busy === 'portal' ? 'Opening…' : 'Invoices and payment method'}<Icon name="external" size={14} /></button> : null}</header>

    {returning ? <div className="plan-note billing-wait"><div className="loader" /><span>Confirming your payment with Stripe. This usually takes a few seconds.</span></div> : null}

    {billing.balance_cents < 0
      ? <div className="plan-note billing-short"><Icon name="alert" size={18} /><span><b>Your credit is {usd(-billing.balance_cents)} below zero.</b> Your videos reached view bonuses that used more than your free credit. Until you add credit, you can’t send a new campaign and your live campaigns take no new creators.</span></div>
      : billing.available_cents < 0 ? <div className="plan-note billing-short"><Icon name="alert" size={18} /><span><b>Your campaigns need {usd(-billing.available_cents)} more credit.</b> Their work cost more than they set aside. Add credit so it keeps going.</span></div> : null}

    <section className="card billing-panel">
      <PlanLine data={data} subscription={billing.subscription} />
      <div className="billing-panel-body">
        <div className="billing-credit">
          <p className="eyebrow">Free for new creators<InfoTip text="What the next creators you bring in can use. A view bonus comes off it when a video reaches it." /></p>
          <b className="billing-free">{usd(Math.max(0, billing.available_cents))}</b>
          <dl className="billing-figures">
            <div><dt>Set aside for campaigns<InfoTip text="The price of the videos your creators are making. You pay only for the work you approve; the rest comes back when the campaign ends." /></dt><dd>{usd(billing.held_cents)}</dd></div>
            <div><dt>Credit in all</dt>{billing.balance_cents < 0 ? <dd className="owed">−{usd(-billing.balance_cents)}</dd> : <dd>{usd(billing.balance_cents)}</dd>}</div>
            {pending.length ? <div><dt>On its way by bank</dt><dd>{usd(pending.reduce((sum, item) => sum + item.amount_cents, 0))}</dd></div> : null}
          </dl>
        </div>

        {billing.payments_enabled ? <form className="billing-buy" onSubmit={(event) => void pay(event)}>
          <h2>Add credit<InfoTip text={`${usd(min, true)} to ${usd(max, true)}. Tax is added where it applies. Pay by card or bank; above ${usd(cardMax, true)}, by bank.`} /></h2>
          <div className="billing-presets">{billing.top_up.presets_cents.map((preset) => <button type="button" key={preset} className={cents === preset ? 'active' : ''} onClick={() => setAmount(String(preset / 100))}>{usd(preset, true)}</button>)}</div>
          <div className="billing-pay">
            <label className="brand-field"><div className="money-input"><input aria-label="Amount in US dollars" autoFocus={params.has('add')} inputMode="numeric" value={amount} onChange={(event) => setAmount(event.target.value.replace(/[^0-9]/g, ''))} placeholder={String(min / 100)} /><span>USD</span></div></label>
            <button className="btn btn-g" disabled={busy === 'pay' || cents < min || cents > max}>{busy === 'pay' ? 'Opening checkout…' : cents ? `Pay ${usd(cents, true)}` : 'Continue to payment'}<Icon name="lock" size={15} /></button>
          </div>
          {/* The limits show only when the amount is outside them; a bank-only amount says so before Stripe. */}
          {cents > 0 && (cents < min || cents > max)
            ? <small className="billing-limit">From {usd(min, true)} to {usd(max, true)}.</small>
            : cents > cardMax ? <small className="billing-limit bank">This amount is paid by bank: ACH debit or bank transfer.</small> : null}
          <p className="billing-terms">For business use. Credit doesn’t expire.<InfoTip wide text="Credit is a prepayment for Earn services under your agreement: it doesn’t expire, can’t be passed on or exchanged for cash, and you get an invoice for every purchase." /></p>
        </form> : <div className="billing-buy"><h2>Add credit</h2><p className="muted">Online payment is being set up. Until then, our team invoices your campaigns directly.</p></div>}
      </div>
    </section>

    <section className="card brand-section billing-section"><h2>Payments</h2>
      {billing.payments.length ? <div className="billing-list">{billing.payments.map((payment) => <div className="billing-row" key={payment.id}>
        <span className="billing-row-icon"><Icon name="receipt" size={18} /></span>
        <span className="billing-row-main"><b>{usd(payment.amount_cents)} credit{payment.kind === 'plan' ? ' · monthly plan' : ''}{payment.tax_cents ? <small> + {usd(payment.tax_cents)} tax</small> : null}</b><small>{day(payment.paid_at || payment.created_at)}{payment.invoice_number ? ` · Invoice ${payment.invoice_number}` : ''}{payment.refunded_cents ? ` · ${usd(payment.refunded_cents)} refunded` : ''}</small></span>
        <span className={`status-pill ${payment.status === 'paid' ? 'active' : payment.status === 'failed' ? 'rejected' : 'in_review'}`}>{PAYMENT_LABEL[payment.status]}</span>
        {payment.invoice_pdf ? <a className="btn btn-light btn-sm" href={payment.invoice_pdf} target="_blank" rel="noopener"><Icon name="download" size={15} />PDF</a> : <span />}
      </div>)}</div> : <div className="empty-state"><Icon name="receipt" size={28} /><b>No payments yet</b><span>Your invoices will be listed here.</span></div>}
    </section>

    <section className="card brand-section billing-section"><h2>Credit history</h2>
      {billing.entries.length ? <div className="billing-list">{billing.entries.map((entry) => <div className="billing-row ledger" key={entry.id}>
        <span className="billing-row-main"><b>{entryLabel(entry)}{entry.campaign_title ? ` · ${entry.campaign_title}` : ''}</b><small>{day(entry.created_at)}{entry.kind === 'adjustment' && entry.note ? ` · ${entry.note.replace(GIFT_NOTE, '')}` : ''}</small></span>
        <b className={`billing-amount${entry.amount_cents > 0 ? ' plus' : ''}`}>{entry.amount_cents > 0 ? '+' : '−'}{usd(Math.abs(entry.amount_cents))}</b>
      </div>)}</div> : <div className="empty-state"><Icon name="wallet" size={28} /><b>Nothing yet</b><span>Purchases and the work they pay for show up here.</span></div>}
    </section>
  </div>;
}
